import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';

import { modelOf } from '../../../prop-types';
import ProductStore from '../../../store/ProductStore';
import { paramsToQueryIdentifier } from '../../../util/query';
import ContentForState from '../../loader/ContentForState';
import ProductList from '../ProductList';
import useFetchData from './hook';
import ProductListSchema from "../../schema/product/ProductListSchema";
import UIStore from "../../../store/UIStore";
import CommonPage from "../../../types/CommonPage";

const SimpleProductList = ({
  productStore,
  uiStore,
  searchParams,
  listId,
  name,
  listComponent: ListComponent,
  completeListingLink,
  preloadOnly,
  allSections,
  singleRowMode,
  requireCanonicalLink,
  cardSettings,
  productImageSettings,
  ...rest
}) => {
  const getQueryIdentifier = () => {
    return paramsToQueryIdentifier(searchParams);
  };

  const getCurrentSearchResult = () => {
    return productStore.productQueryResults.get(getQueryIdentifier());
  };

  const getCurrentSearchState = () => {
    return productStore.productQueryStates.get(getQueryIdentifier());
  };

  const params = {
    searchParams,
    allSections,
    state: getCurrentSearchState(),
  };

  useFetchData({ productStore, params });

  if (preloadOnly) {
    return null;
  }

  return (
    <div className="SimpleProductList">
      <ContentForState
        state={getCurrentSearchState()}
        error={productStore.lastError}
        forLoaded={() => {
          const products = getCurrentSearchResult().data;
          const addSchema = uiStore.currentPage === CommonPage.HOME
            || uiStore.currentPage === CommonPage.SECTION;
          return (
            <>
              <ListComponent
                products={products}
                listId={listId}
                name={name}
                completeListingLink={!singleRowMode ? completeListingLink : null}
                cardSettings={{
              requireCanonicalLink,
              ...cardSettings,
                }}
            productImageSettings={productImageSettings}
                {...rest}
              />
              {addSchema && <ProductListSchema products={products} />}
            </>
          );
        }}
      />
    </div>
  );
};

SimpleProductList.propTypes = {
  productStore: modelOf(ProductStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  searchParams: PropTypes.object.isRequired,
  listId: PropTypes.string,
  name: PropTypes.string,
  preloadOnly: PropTypes.bool,
  allSections: PropTypes.bool,
  singleRowMode: PropTypes.bool,
  requireCanonicalLink: PropTypes.bool,
  cardSettings: PropTypes.object,
  productImageSettings: PropTypes.object,
  completeListingLink: PropTypes.node,
  listComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

SimpleProductList.defaultProps = {
  listComponent: ProductList,
  preloadOnly: false,
  allSections: false,
  singleRowMode: false,
  requireCanonicalLink: false,
};

export default inject('productStore', 'uiStore')(observer(SimpleProductList));
